import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtINR, formatApiError, customerAddressBook, formatAddressLine } from "@/lib/api";
import { StatusBadge } from "@/pages/Dashboard";
import { toast } from "sonner";
import {
  ArrowLeft,
  Copy,
  Truck,
  FileText,
  Loader2,
  Calculator,
  ChevronDown,
  X,
  Rocket,
  ExternalLink,
  Plus,
  Search,
  Mail,
  Package,
  UserPlus,
  Pencil,
  CalendarClock,
} from "lucide-react";

const WHATSAPP_GREEN = "#25D366";

const STATUS_OPTIONS = [
  { v: "preorder", l: "PRE-ORDER" },
  { v: "awaiting_address", l: "AWAITING ADDRESS" },
  { v: "address_received", l: "READY TO PACK" },
  { v: "packed", l: "PACKED" },
  { v: "shipped", l: "SHIPPED" },
  { v: "delivered", l: "DELIVERED" },
  { v: "cancelled", l: "CANCELLED" },
];

const COURIER_OPTIONS = [
  { value: "delhivery", label: "Delhivery" },
  { value: "amazon", label: "Amazon Shipping" },
  { value: "xpressbees", label: "Xpressbees" },
  { value: "vichare", label: "Vichare" },
];

function courierLabel(courier) {
  return COURIER_OPTIONS.find((c) => c.value === courier)?.label || courier || "Courier";
}

function publicTrackingUrl(courier, awb) {
  if (!awb) return null;
  const id = encodeURIComponent(String(awb).trim());
  if (courier === "delhivery") {
    return `https://www.delhivery.com/track/package/${id}`;
  }
  if (courier === "xpressbees") {
    return `https://www.xpressbees.com/track?isawb=Yes&trackid=${id}`;
  }
  if (courier === "vichare") {
    return "https://portal.vichareloginext.com/tracking";
  }
  if (courier === "amazon") {
    return `https://track.amazon.in/tracking/${id}`;
  }
  return null;
}

function formatScanWhen(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN");
}

function CourierAwbFields({ courier, awb, onCourier, onAwb, courierTestId, awbTestId }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label htmlFor={courierTestId} className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
          DELIVERY SERVICE
        </label>
        <select
          id={courierTestId}
          data-testid={courierTestId}
          value={courier}
          onChange={(e) => onCourier(e.target.value)}
          className="w-full h-12 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
        >
          {COURIER_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor={awbTestId} className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
          AWB / TRACKING NUMBER
        </label>
        <input
          id={awbTestId}
          data-testid={awbTestId}
          value={awb}
          onChange={(e) => onAwb(e.target.value)}
          placeholder="AWB / tracking ID"
          className="w-full h-12 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm font-mono focus:outline-none focus:border-[color:var(--mecha-red)]"
        />
      </div>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [shipping, setShipping] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [dlInvoice, setDlInvoice] = useState(false);

  const [calc, setCalc] = useState({
    weight: 500,
    length: "",
    width: "",
    height: "",
    shipping_mode: "Surface",
    calculating: false,
    result: null,
    error: null,
  });

  const [manifestOpen, setManifestOpen] = useState(false);
  const [manifestForm, setManifestForm] = useState(null);
  const [manifesting, setManifesting] = useState(false);
  const [manifestError, setManifestError] = useState(null);
  const [trackCourier, setTrackCourier] = useState("delhivery");
  const [trackNumber, setTrackNumber] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  const [addItemsOpen, setAddItemsOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [productQ, setProductQ] = useState("");
  const [addQty, setAddQty] = useState({});
  const [addingItems, setAddingItems] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [custQ, setCustQ] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [editCustOpen, setEditCustOpen] = useState(false);
  const [editCustForm, setEditCustForm] = useState(null);
  const [savingCust, setSavingCust] = useState(false);
  const [fulfilling, setFulfilling] = useState(false);
  const [liveTracking, setLiveTracking] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);

  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/orders/${id}`);
      setOrder(data);
      if (data?.created_at) setOrderDate(String(data.created_at).slice(0, 10));
      if (data?.courier) setTrackCourier(data.courier);
      if (data?.awb_number) setTrackNumber(data.awb_number);
      try {
        const { data: crm } = await api.get("/api/customers");
        setCustomers(crm || []);
      } catch {
        /* address picker stays hidden if CRM list fails */
      }
    } catch (e) {
      toast.error("Failed to load order");
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const loadTracking = useCallback(async () => {
    if (!id) return;
    setTrackingLoading(true);
    try {
      const { data } = await api.get(`/api/orders/${id}/tracking`);
      setLiveTracking(data);
    } catch {
      setLiveTracking(null);
    } finally {
      setTrackingLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (order?.status === "shipped" || order?.status === "delivered") {
      loadTracking();
    } else {
      setLiveTracking(null);
    }
  }, [order?.status, order?.awb_number, loadTracking]);

  const addressLink = order?.address_token
    ? `${window.location.origin}/address/${order.address_token}`
    : null;

  const copyLink = async () => {
    if (!addressLink) return;
    await navigator.clipboard.writeText(addressLink);
    toast.success("Link copied to clipboard");
  };

  const ship = async () => {
    const awb = trackNumber.trim();
    if (!awb) return toast.error("Enter a tracking number");
    setShipping(true);
    try {
      await api.post(`/api/orders/${id}/ship`, {
        courier: trackCourier,
        awb_number: awb,
      });
      toast.success(`Shipped via ${courierLabel(trackCourier)}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setShipping(false);
    }
  };

  const trackingDirty =
    Boolean(order) &&
    (trackCourier !== (order.courier || "delhivery") ||
      trackNumber.trim() !== String(order.awb_number || "").trim());

  const updateShipment = async () => {
    const awb = trackNumber.trim();
    if (!awb) return toast.error("Enter a tracking number");
    setShipping(true);
    try {
      await api.patch(`/api/orders/${id}/shipment`, {
        courier: trackCourier,
        awb_number: awb,
      });
      toast.success(`Tracking updated to ${courierLabel(trackCourier)} · ${awb}`);
      await load();
      loadTracking();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setShipping(false);
    }
  };

  const canAddItems =
    order && !["cancelled", "delivered"].includes(order.status);

  const openAddItems = async () => {
    setAddItemsOpen(true);
    setProductQ("");
    setAddQty({});
    try {
      const { data } = await api.get("/api/products");
      setProducts(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load products");
    }
  };

  const setQtyFor = (productId, value) => {
    const n = Math.max(0, parseInt(value, 10) || 0);
    setAddQty((prev) => ({ ...prev, [productId]: n }));
  };

  const submitAddItems = async () => {
    const items = Object.entries(addQty)
      .filter(([, qty]) => qty > 0)
      .map(([product_id, quantity]) => ({ product_id, quantity }));
    if (!items.length) return toast.error("Select at least one product quantity");
    setAddingItems(true);
    try {
      const { data } = await api.post(`/api/orders/${id}/items`, { items });
      setOrder(data);
      toast.success("Products added to order");
      setAddItemsOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAddingItems(false);
    }
  };

  const resendConfirmation = async () => {
    setResendingEmail(true);
    try {
      const { data } = await api.post(`/api/orders/${id}/resend-email`, null, {
        params: { kind: "confirmation" },
      });
      toast.success(`Confirmation emailed to ${data.to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setResendingEmail(false);
    }
  };

  const canEditCustomer =
    order && !["cancelled", "delivered", "shipped"].includes(order.status);

  const canAttachCustomer =
    order &&
    !order.customer_id &&
    canEditCustomer;

  const openAttachCustomer = async () => {
    setAttachOpen(true);
    setCustQ("");
    try {
      const { data } = await api.get("/api/customers");
      setCustomers(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load customers");
    }
  };

  const attachCustomer = async (customer, addressId) => {
    if (!customer?.id) return;
    setAttaching(true);
    try {
      const payload = { customer_id: customer.id };
      if (addressId && addressId !== "legacy") payload.address_id = addressId;
      const { data } = await api.post(`/api/orders/${id}/attach-customer`, payload);
      setOrder(data);
      setAttachOpen(false);
      toast.success(order?.customer_id ? `Switched to ${customer.name}` : `Linked ${customer.name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAttaching(false);
    }
  };

  const applySavedAddress = async (addr) => {
    if (!addr?.id) return;
    setSavingCust(true);
    try {
      const { data } = await api.patch(`/api/orders/${id}/customer`, {
        address_id: addr.id === "legacy" ? undefined : addr.id,
        address:
          addr.id === "legacy"
            ? {
                line1: addr.line1 || "",
                line2: addr.line2 || "",
                city: addr.city || "",
                state: addr.state || "",
                pincode: addr.pincode || "",
                landmark: addr.landmark || "",
              }
            : undefined,
        sync_crm: false,
      });
      setOrder(data);
      toast.success(`Shipping set to ${addr.label || "saved address"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingCust(false);
    }
  };

  const openEditCustomer = () => {
    if (!order) return;
    const cs = order.customer_snapshot || {};
    const a = cs.address || {};
    const book = customerAddressBook(
      customers.find((c) => String(c.id) === String(order.customer_id))
    );
    const match =
      book.find((saved) => saved.id && saved.id === cs.address_id) ||
      book.find(
        (saved) =>
          saved.pincode === a.pincode &&
          (saved.line1 || "").trim().toLowerCase() === (a.line1 || "").trim().toLowerCase()
      ) ||
      book.find((saved) => saved.is_default) ||
      book[0];
    setEditCustForm({
      name: cs.name || "",
      phone: cs.phone || "",
      email: cs.email || "",
      line1: a.line1 || "",
      line2: a.line2 || "",
      city: a.city || "",
      state: a.state || "",
      pincode: a.pincode || "",
      landmark: a.landmark || "",
      address_id: match?.id || (book.length ? "new" : ""),
      label: cs.address_label || match?.label || "Home",
      is_default: Boolean(match?.is_default),
      sync_crm: Boolean(order.customer_id),
    });
    setEditCustOpen(true);
  };

  const saveEditCustomer = async (e) => {
    e.preventDefault();
    if (!editCustForm) return;
    const name = editCustForm.name.trim();
    const phone = editCustForm.phone.trim();
    if (!name) return toast.error("Name is required");
    if (!phone) return toast.error("Phone is required");

    const hasAddress = [editCustForm.line1, editCustForm.city, editCustForm.state, editCustForm.pincode].some(
      (v) => String(v || "").trim()
    );
    if (hasAddress) {
      if (
        !editCustForm.line1.trim() ||
        !editCustForm.city.trim() ||
        !editCustForm.state.trim() ||
        !editCustForm.pincode.trim()
      ) {
        return toast.error("Address needs line1, city, state, and pincode");
      }
    }

    setSavingCust(true);
    try {
      const body = {
        name,
        phone,
        email: editCustForm.email.trim(),
        address: hasAddress
          ? {
              line1: editCustForm.line1.trim(),
              line2: editCustForm.line2.trim(),
              city: editCustForm.city.trim(),
              state: editCustForm.state.trim(),
              pincode: editCustForm.pincode.trim(),
              landmark: editCustForm.landmark.trim(),
            }
          : null,
        sync_crm: Boolean(editCustForm.sync_crm && order.customer_id),
      };
      if (hasAddress && editCustForm.address_id) {
        body.address_id = editCustForm.address_id;
        body.address_label = (editCustForm.label || "").trim() || "Home";
        body.address_is_default = Boolean(editCustForm.is_default);
      }
      const { data } = await api.patch(`/api/orders/${id}/customer`, body);
      setOrder(data);
      try {
        const { data: crm } = await api.get("/api/customers");
        setCustomers(crm || []);
      } catch {
        /* keep existing customer list */
      }
      setEditCustOpen(false);
      toast.success("Customer updated on order");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingCust(false);
    }
  };

  const openManifest = () => {
    if (!order) return;
    const cs = order.customer_snapshot || {};
    const addr = cs.address || {};
    const fullAddr = [addr.line1, addr.line2, addr.landmark].filter(Boolean).join(", ");
    const qty = order.items.reduce((s, i) => s + i.quantity, 0);
    const desc = order.items.map((i) => `${i.name} x${i.quantity}`).join(", ").slice(0, 150);
    setManifestForm({
      name: cs.name || "",
      phone: cs.phone || "",
      address: fullAddr,
      pincode: addr.pincode || "",
      city: addr.city || "",
      state: addr.state || "",
      weight_g: 500,
      length_cm: 20,
      width_cm: 15,
      height_cm: 10,
      quantity: qty || 1,
      products_desc: desc || "Anime Collectibles",
      payment_mode: "Prepaid",
      cod_amount: 0,
      total_amount: order.total,
      shipping_mode: "Surface",
    });
    setManifestError(null);
    setManifestOpen(true);
  };

  const submitManifest = async () => {
    if (!manifestForm) return;
    setManifesting(true);
    setManifestError(null);
    try {
      const payload = {
        ...manifestForm,
        weight_g: parseInt(manifestForm.weight_g, 10) || 500,
        length_cm: parseFloat(manifestForm.length_cm) || 0,
        width_cm: parseFloat(manifestForm.width_cm) || 0,
        height_cm: parseFloat(manifestForm.height_cm) || 0,
        quantity: parseInt(manifestForm.quantity, 10) || 1,
        cod_amount: parseFloat(manifestForm.cod_amount) || 0,
        total_amount: parseFloat(manifestForm.total_amount) || 0,
      };
      const { data } = await api.post(`/api/orders/${id}/manifest`, payload);
      toast.success(`Manifested — AWB ${data.awb_number}`);
      setManifestOpen(false);
      setOrder(data);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setManifestError(msg);
    } finally {
      setManifesting(false);
    }
  };

  const changeStatus = async (nextStatus) => {
    setSavingStatus(true);
    setStatusOpen(false);
    try {
      await api.patch(`/api/orders/${id}/status`, { status: nextStatus });
      toast.success(`Status changed to ${nextStatus.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingStatus(false);
    }
  };

  const fulfillPreorder = async () => {
    setFulfilling(true);
    try {
      const { data } = await api.post(`/api/orders/${id}/fulfill-preorder`);
      setOrder(data);
      toast.success("Pre-order fulfilled — stock deducted, ready to pack/ship");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setFulfilling(false);
    }
  };

  const saveOrderDate = async () => {
    if (!orderDate) return toast.error("Pick a date");
    setSavingDate(true);
    try {
      await api.patch(`/api/orders/${id}/date`, { date: orderDate });
      toast.success("Order date updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingDate(false);
    }
  };

  const downloadInvoice = async () => {
    setDlInvoice(true);
    try {
      const res = await api.get(`/api/public/invoice/${id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${order.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download invoice");
    } finally {
      setDlInvoice(false);
    }
  };

  const calcShipping = async () => {
    const pin = order?.customer_snapshot?.address?.pincode;
    if (!pin) {
      setCalc((c) => ({ ...c, error: "Customer pincode not available yet" }));
      return;
    }
    setCalc((c) => ({ ...c, calculating: true, error: null, result: null }));
    try {
      const payload = {
        destination_pincode: pin,
        weight_grams: parseInt(calc.weight, 10) || 500,
        payment_mode: "Pre-paid",
        shipping_mode: calc.shipping_mode || "Surface",
      };
      const L = parseFloat(calc.length);
      const W = parseFloat(calc.width);
      const H = parseFloat(calc.height);
      if (L > 0 && W > 0 && H > 0) {
        payload.length_cm = L;
        payload.width_cm = W;
        payload.height_cm = H;
      }
      const { data } = await api.post("/api/shipping/rate", payload);
      setCalc((c) => ({ ...c, calculating: false, result: data }));
    } catch (e) {
      setCalc((c) => ({
        ...c,
        calculating: false,
        error: formatApiError(e.response?.data?.detail) || e.message,
      }));
    }
  };

  const trackingUrl =
    liveTracking?.tracking_url || publicTrackingUrl(order?.courier, order?.awb_number);
  const shippedCourierLabel = liveTracking?.courier_label || courierLabel(order?.courier);

  const waMessage = () => {
    if (!order?.customer_snapshot?.phone || !order.awb_number || !trackingUrl) return "";
    const phone = order.customer_snapshot.phone.replace(/[^\d]/g, "");
    const msg = encodeURIComponent(
      `Hi ${order.customer_snapshot.name}!\n\nYour Swagg Squad order *${order.order_number}* has shipped 🚚\n\nCourier: ${shippedCourierLabel}\nAWB / Tracking: ${order.awb_number}\nTrack here: ${trackingUrl}\n\nThanks for your order!`
    );
    return `https://wa.me/${phone.startsWith("91") ? phone : "91" + phone}?text=${msg}`;
  };

  const shareAddressLinkWA = () => {
    if (!order || !addressLink) return "";
    const isPre = order.status === "preorder";
    const msg = encodeURIComponent(
      isPre
        ? `Hi! Your Swagg Squad pre-order *${order.order_number}* is booked ✅\n\nPlease fill your shipping address here so we can ship as soon as it arrives:\n${addressLink}`
        : `Hi! Your Swagg Squad order *${order.order_number}* is confirmed ✅\n\nPlease fill your shipping address here:\n${addressLink}\n\nWe'll ship it out as soon as we receive it!`
    );
    return `https://wa.me/?text=${msg}`;
  };

  if (!order)
    return (
      <div className="p-6 sm:p-12 text-zinc-500 font-mono text-xs tracking-[0.3em]">
        LOADING...
      </div>
    );

  const cust = order.customer_snapshot;
  const addr = cust?.address;
  const linkedBook = customerAddressBook(
    customers.find((c) => String(c.id) === String(order.customer_id))
  );

  return (
    <div className="p-4 sm:p-6 lg:p-12">
      <button
        onClick={() => nav("/orders")}
        data-testid="back-btn"
        className="mb-6 flex items-center gap-2 font-mono text-xs tracking-[0.3em] text-zinc-500 hover:text-white"
      >
        <ArrowLeft className="h-3 w-3" /> BACK TO ORDERS
      </button>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end justify-between gap-4 mb-8 sm:mb-10">
        <div>
          <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
            // ORDER
          </div>
          <h1
            data-testid="order-number-heading"
            className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 leading-none"
          >
            {order.order_number}
          </h1>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            {/* Status dropdown */}
            <div className="relative">
              <button
                data-testid="status-dropdown-trigger"
                onClick={() => setStatusOpen((s) => !s)}
                disabled={savingStatus}
                className="flex items-center gap-2 disabled:opacity-60"
              >
                <StatusBadge status={order.status} />
                <ChevronDown className="h-3 w-3 text-zinc-400" />
              </button>
              {statusOpen && (
                <div className="absolute top-full left-0 mt-2 z-20 card-brutal min-w-[220px] shadow-lg">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.v}
                      data-testid={`set-status-${s.v}`}
                      onClick={() => changeStatus(s.v)}
                      disabled={s.v === order.status}
                      className={`w-full text-left px-4 py-3 text-xs font-mono tracking-widest border-b border-zinc-900 last:border-0 hover:bg-zinc-900 ${
                        s.v === order.status ? "text-[color:var(--mecha-red)] bg-zinc-900/50 cursor-default" : "text-zinc-300"
                      }`}
                    >
                      {s.l}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                ORDER DATE
              </label>
              <input
                data-testid="order-date-input"
                type="date"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                className="h-9 bg-zinc-950 border border-zinc-800 px-2 text-white text-xs font-mono focus:outline-none focus:border-[color:var(--mecha-red)]"
              />
              <button
                data-testid="order-date-save"
                type="button"
                onClick={saveOrderDate}
                disabled={
                  savingDate ||
                  !orderDate ||
                  orderDate === String(order.created_at || "").slice(0, 10)
                }
                className="h-9 px-3 border border-zinc-700 hover:border-white text-[10px] font-mono tracking-widest uppercase disabled:opacity-40"
              >
                {savingDate ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save date"}
              </button>
              {order.expected_date && (
                <span className="font-mono text-[10px] tracking-widest text-[#a78bfa]">
                  ETA {order.expected_date}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 w-full sm:w-auto flex-wrap">
          <button
            data-testid="resend-confirmation-email"
            type="button"
            onClick={resendConfirmation}
            disabled={resendingEmail || !order.customer_snapshot?.email}
            title={
              order.customer_snapshot?.email
                ? `Resend to ${order.customer_snapshot.email}`
                : "No customer email on file"
            }
            className="btn-brutal flex-1 sm:flex-none h-12 px-5 border border-zinc-600 text-white hover:border-white text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {resendingEmail ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            Resend email
          </button>
          <button
            data-testid="download-invoice"
            onClick={downloadInvoice}
            disabled={dlInvoice}
            className="btn-brutal flex-1 sm:flex-none h-12 px-5 border border-white text-white hover:bg-white hover:text-black text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {dlInvoice ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            Invoice PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: items + shipping + calc */}
        <div className="lg:col-span-2 space-y-6">
          <section className="card-brutal">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between gap-3">
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                ITEMS
              </div>
              {canAddItems && (
                <button
                  type="button"
                  data-testid="add-order-items"
                  onClick={openAddItems}
                  className="h-9 px-3 border border-zinc-700 hover:border-white text-[10px] font-mono tracking-widest uppercase flex items-center gap-2"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add products
                </button>
              )}
            </div>
            <div className="divide-y divide-zinc-900">
              {order.items.map((i, idx) => (
                <div key={idx} className="p-5 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{i.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-1">
                      {i.sku} · Qty {i.quantity} · {fmtINR(i.price)} ea
                    </div>
                  </div>
                  <div className="font-mono font-bold">
                    {fmtINR(i.price * i.quantity)}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-zinc-800 space-y-2">
              <Row label="Subtotal" value={fmtINR(order.subtotal)} />
              <Row label="Discount" value={`- ${fmtINR(order.discount)}`} />
              <Row label={`Tax (${order.tax_rate}%)`} value={fmtINR(order.tax)} />
              <Row label="Shipping" value={fmtINR(order.shipping_charges || 0)} />
              <div className="flex justify-between items-end pt-3 border-t border-zinc-800 mt-2">
                <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                  TOTAL
                </span>
                <span className="font-display text-2xl text-[color:var(--mecha-red)]">
                  {fmtINR(order.total)}
                </span>
              </div>
            </div>
          </section>

          {/* Shipping */}
          <section className="card-brutal">
            <div className="p-5 border-b border-zinc-800 font-mono text-[10px] tracking-[0.3em] text-zinc-500">
              SHIPPING
            </div>
            <div className="p-5">
              {order.status === "preorder" && (
                <div className="space-y-4">
                  <div className="border border-[#a78bfa]/40 bg-[#a78bfa]/10 p-4 space-y-2">
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#a78bfa]">
                      PRE-ORDER · STOCK NOT DEDUCTED
                    </div>
                    <div className="text-sm text-zinc-300">
                      Customer paid at the counter. Fulfill this when the item arrives —
                      that deducts stock and moves it into the pack/ship flow.
                    </div>
                    {order.expected_date && (
                      <div className="flex items-center gap-2 font-mono text-xs text-zinc-400">
                        <CalendarClock className="h-3.5 w-3.5" />
                        Expected {order.expected_date}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid="fulfill-preorder-btn"
                    disabled={fulfilling}
                    onClick={fulfillPreorder}
                    className="btn-brutal w-full h-12 bg-white text-black hover:bg-zinc-200 text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {fulfilling ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Package className="h-4 w-4" />
                    )}
                    Stock arrived — fulfill pre-order
                  </button>
                  {!order.address_submitted_at && addressLink && (
                    <div className="space-y-3 pt-2">
                      <div className="text-sm text-zinc-300">
                        Collect the delivery address now so you can ship as soon as stock lands:
                      </div>
                      <div
                        data-testid="address-link-box"
                        className="flex items-stretch border border-zinc-800"
                      >
                        <div className="flex-1 px-4 py-3 font-mono text-xs text-[color:var(--neon-cyan)] truncate">
                          {addressLink}
                        </div>
                        <button
                          data-testid="copy-link"
                          onClick={copyLink}
                          className="px-4 border-l border-zinc-800 hover:bg-white hover:text-black"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <a
                          data-testid="share-link-wa"
                          href={shareAddressLinkWA()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 border-l border-zinc-800 hover:opacity-80 flex items-center font-mono text-[10px] tracking-widest text-white"
                          style={{ background: WHATSAPP_GREEN }}
                        >
                          SHARE WA
                        </a>
                      </div>
                    </div>
                  )}
                  {order.address_submitted_at && (
                    <div className="text-xs text-zinc-500 font-mono tracking-widest">
                      ADDRESS ON FILE · WILL GO TO READY TO PACK AFTER FULFILL
                    </div>
                  )}
                </div>
              )}

              {order.status === "awaiting_address" && (
                <div className="space-y-4">
                  <div className="text-sm text-zinc-300">
                    Share this link with the customer to collect their delivery address:
                  </div>
                  <div
                    data-testid="address-link-box"
                    className="flex items-stretch border border-zinc-800"
                  >
                    <div className="flex-1 px-4 py-3 font-mono text-xs text-[color:var(--neon-cyan)] truncate">
                      {addressLink}
                    </div>
                    <button
                      data-testid="copy-link"
                      onClick={copyLink}
                      className="px-4 border-l border-zinc-800 hover:bg-white hover:text-black"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <a
                      data-testid="share-link-wa"
                      href={shareAddressLinkWA()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 border-l border-zinc-800 hover:opacity-80 flex items-center font-mono text-[10px] tracking-widest text-white"
                      style={{ background: WHATSAPP_GREEN }}
                    >
                      SHARE WA
                    </a>
                  </div>
                </div>
              )}

              {(order.status === "address_received" || order.status === "packed") && (
                <div className="space-y-4">
                  {order.status === "address_received" && (
                    <div className="space-y-3">
                      <div className="text-sm text-zinc-300">
                        Address received. Pack the order, then mark it packed or add tracking to ship.
                      </div>
                      <button
                        type="button"
                        data-testid="mark-packed-btn"
                        disabled={savingStatus}
                        onClick={() => changeStatus("packed")}
                        className="btn-brutal w-full h-12 border border-[color:var(--neon-cyan)] text-[color:var(--neon-cyan)] hover:bg-[color:var(--neon-cyan)] hover:text-black text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        {savingStatus ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Package className="h-4 w-4" />
                        )}
                        Mark as Packed
                      </button>
                      <div className="text-[10px] font-mono tracking-widest text-zinc-500 text-center pt-1">
                        OR SHIP DIRECTLY
                      </div>
                    </div>
                  )}
                  {order.status === "packed" && (
                    <div className="text-sm text-zinc-300">
                      Order is packed. Add the courier and tracking number to mark it shipped:
                    </div>
                  )}
                  <CourierAwbFields
                    courier={trackCourier}
                    awb={trackNumber}
                    onCourier={setTrackCourier}
                    onAwb={setTrackNumber}
                    courierTestId="ship-courier"
                    awbTestId="ship-tracking"
                  />
                  <button
                    data-testid="ship-save-btn"
                    disabled={shipping || !trackNumber.trim()}
                    onClick={ship}
                    className="btn-brutal w-full h-14 bg-[color:var(--mecha-red)] text-white font-bold tracking-widest uppercase text-sm hover:bg-[color:var(--mecha-red-hover)] flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {shipping ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Truck className="h-4 w-4" />
                    )}
                    Save Tracking & Mark Shipped
                  </button>
                  <div className="text-[10px] font-mono tracking-widest text-zinc-500 text-center pt-2">
                    OR CREATE A LIVE DELHIVERY WAYBILL
                  </div>
                  <button
                    data-testid="manifest-btn"
                    onClick={openManifest}
                    className="btn-brutal w-full h-12 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2"
                  >
                    <Rocket className="h-4 w-4" />
                    Manifest on Delhivery
                  </button>
                </div>
              )}

              {(order.status === "shipped" || order.status === "delivered") && (
                <div className="space-y-4">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                      CHANGE COURIER / AWB
                    </div>
                    <p className="text-sm text-zinc-400 mt-2">
                      Wrong service or tracking number? Edit both and save. Live scans refresh after save.
                    </p>
                  </div>
                  <CourierAwbFields
                    courier={trackCourier}
                    awb={trackNumber}
                    onCourier={setTrackCourier}
                    onAwb={setTrackNumber}
                    courierTestId="update-courier"
                    awbTestId="update-tracking"
                  />
                  <button
                    type="button"
                    data-testid="update-tracking-btn"
                    disabled={shipping || !trackNumber.trim() || !trackingDirty}
                    onClick={updateShipment}
                    className="btn-brutal w-full h-12 bg-white text-black hover:bg-zinc-200 text-xs font-mono tracking-widest uppercase disabled:opacity-40 flex items-center justify-center gap-2"
                  >
                    {shipping ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Pencil className="h-4 w-4" />
                    )}
                    {trackingDirty ? "Save courier & AWB" : "Courier & AWB saved"}
                  </button>
                  <Field
                    label="SHIPPED AT"
                    value={new Date(order.shipped_at).toLocaleString("en-IN")}
                  />

                  {trackingUrl || liveTracking?.scans?.length || liveTracking?.status ? (
                    <div className="border border-zinc-800 bg-zinc-950 p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                            LIVE TRACKING STATUS
                          </div>
                          {liveTracking?.status ? (
                            <div className="text-sm text-white mt-2">
                              {liveTracking.status}
                              {liveTracking.location ? ` · ${liveTracking.location}` : ""}
                            </div>
                          ) : (
                            <div className="text-sm text-zinc-300 mt-2">
                              Latest scans for{" "}
                              <span className="font-mono text-white">{order.awb_number}</span>
                              {trackingLoading ? " — checking…" : "."}
                            </div>
                          )}
                          {liveTracking?.status_at && (
                            <div className="text-xs text-zinc-500 mt-1">
                              {formatScanWhen(liveTracking.status_at)}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={loadTracking}
                          disabled={trackingLoading}
                          className="h-9 px-3 border border-zinc-700 hover:border-white text-[10px] font-mono tracking-widest uppercase disabled:opacity-40"
                        >
                          {trackingLoading ? "…" : "Refresh"}
                        </button>
                      </div>
                      {(liveTracking?.scans || []).length > 0 && (
                        <ol className="space-y-2 border-t border-zinc-800 pt-3">
                          {liveTracking.scans.map((scan, idx) => (
                            <li key={`${scan.at}-${idx}`} className="text-sm text-zinc-200">
                              <div>{scan.status || "Update"}</div>
                              <div className="text-[10px] font-mono tracking-widest text-zinc-500">
                                {[formatScanWhen(scan.at), scan.location].filter(Boolean).join(" · ")}
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                      {trackingUrl && (
                        <a
                          data-testid="open-tracking-btn"
                          href={trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-brutal w-full h-12 bg-white text-black hover:bg-zinc-200 text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Open {liveTracking?.courier_label || shippedCourierLabel} tracking
                        </a>
                      )}
                    </div>
                  ) : null}

                  {cust?.phone ? (
                    <a
                      data-testid="wa-share-btn"
                      href={waMessage()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-brutal w-full h-12 flex items-center justify-center gap-2 text-white font-bold tracking-widest uppercase text-sm hover:opacity-90"
                      style={{ background: WHATSAPP_GREEN }}
                    >
                      SEND TRACKING VIA WHATSAPP
                    </a>
                  ) : (
                    <div className="text-xs text-zinc-500">
                      Customer phone missing — cannot send WhatsApp
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Delhivery shipping cost calculator */}
          {addr?.pincode && order.status !== "cancelled" && (
            <section className="card-brutal">
              <div className="p-5 border-b border-zinc-800 flex items-center gap-2">
                <Calculator className="h-4 w-4 text-[color:var(--mecha-red)]" />
                <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                  DELHIVERY COST ESTIMATE
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
                      DESTINATION PIN
                    </label>
                    <div className="h-11 border border-zinc-800 px-3 flex items-center font-mono text-sm bg-zinc-950">
                      {addr.pincode}
                    </div>
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
                      ACTUAL WEIGHT (G)
                    </label>
                    <input
                      data-testid="calc-weight"
                      type="number"
                      min="1"
                      value={calc.weight}
                      onChange={(e) => setCalc((c) => ({ ...c, weight: e.target.value }))}
                      className="w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
                    PARCEL DIMENSIONS · CM (OPTIONAL)
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      data-testid="calc-length"
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="L"
                      value={calc.length}
                      onChange={(e) => setCalc((c) => ({ ...c, length: e.target.value }))}
                      className="w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
                    />
                    <input
                      data-testid="calc-width"
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="W"
                      value={calc.width}
                      onChange={(e) => setCalc((c) => ({ ...c, width: e.target.value }))}
                      className="w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
                    />
                    <input
                      data-testid="calc-height"
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="H"
                      value={calc.height}
                      onChange={(e) => setCalc((c) => ({ ...c, height: e.target.value }))}
                      className="w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
                    />
                  </div>
                  <div className="mt-1.5 font-mono text-[10px] tracking-widest text-zinc-500">
                    VOLUMETRIC = L × W × H / 5 &nbsp;·&nbsp; DELHIVERY CHARGES THE HIGHER OF ACTUAL vs VOLUMETRIC
                  </div>
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
                    SHIPPING MODE
                  </label>
                  <select
                    data-testid="calc-shipping-mode"
                    value={calc.shipping_mode}
                    onChange={(e) => setCalc((c) => ({ ...c, shipping_mode: e.target.value }))}
                    className="w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
                  >
                    <option value="Surface">Surface (cheaper, 4-7 days)</option>
                    <option value="Express">Express (air, 2-3 days)</option>
                  </select>
                </div>
                <button
                  data-testid="calc-shipping-btn"
                  onClick={calcShipping}
                  disabled={calc.calculating}
                  className="btn-brutal w-full h-11 border border-white hover:bg-white hover:text-black text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {calc.calculating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Estimate Delhivery Cost"
                  )}
                </button>

                {calc.error && (
                  <div
                    data-testid="calc-error"
                    className="border border-[color:var(--mecha-red)] bg-[color:var(--mecha-red)]/10 p-3 text-xs text-[color:var(--mecha-red-hover)]"
                  >
                    {calc.error}
                  </div>
                )}

                {calc.result && (
                  <div
                    data-testid="calc-result"
                    className="border border-[color:var(--neon-cyan)] p-4 space-y-2 text-sm bg-[color:var(--neon-cyan)]/5"
                  >
                    <div className="grid grid-cols-3 gap-2 pb-2 mb-2 border-b border-[color:var(--neon-cyan)]/30">
                      <WeightPill
                        label="ACTUAL"
                        value={`${calc.result.actual_weight_g || 0} g`}
                        active={calc.result.weight_basis === "actual"}
                      />
                      <WeightPill
                        label="VOLUMETRIC"
                        value={`${calc.result.volumetric_weight_g || 0} g`}
                        active={calc.result.weight_basis === "volumetric"}
                      />
                      <WeightPill
                        label="CHARGED"
                        value={`${calc.result.chargeable_weight_g || 0} g`}
                        emphasize
                      />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Zone</span>
                      <span className="font-mono">{calc.result.zone || "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Gross</span>
                      <span className="font-mono">{fmtINR(calc.result.gross_amount || 0)}</span>
                    </div>
                    <div className="flex justify-between items-end pt-2 border-t border-[color:var(--neon-cyan)]/40">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-400">
                        TOTAL W/ TAX
                      </span>
                      <span
                        data-testid="calc-total"
                        className="font-display text-2xl text-[color:var(--neon-cyan)]"
                      >
                        {fmtINR(calc.result.total_amount || 0)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>

        {/* Right: customer + payment */}        <div className="space-y-6">
          <section className="card-brutal">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between gap-3">
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                CUSTOMER
              </div>
              <div className="flex items-center gap-2">
                {canEditCustomer && cust && (
                  <button
                    type="button"
                    data-testid="edit-order-customer-btn"
                    onClick={openEditCustomer}
                    className="h-9 px-3 border border-zinc-700 hover:border-white text-[10px] font-mono tracking-widest uppercase flex items-center gap-2"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                )}
                {canEditCustomer && (
                  <button
                    type="button"
                    data-testid="attach-customer-btn"
                    onClick={openAttachCustomer}
                    className="h-9 px-3 border border-zinc-700 hover:border-white text-[10px] font-mono tracking-widest uppercase flex items-center gap-2"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    {order.customer_id ? "Change" : "Attach"}
                  </button>
                )}
              </div>
            </div>
            <div className="p-5 space-y-3">
              {cust ? (
                <>
                  <div>
                    <div className="text-lg font-medium">{cust.name}</div>
                    <div className="font-mono text-xs text-zinc-400 mt-1">{cust.phone}</div>
                    {cust.email && (
                      <div className="text-xs text-zinc-500 mt-0.5">{cust.email}</div>
                    )}
                  </div>
                  {addr && (
                    <div className="pt-3 border-t border-zinc-800">
                      <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-2">
                        SHIP TO{cust.address_label ? ` · ${cust.address_label}` : ""}
                      </div>
                      <div className="text-sm leading-relaxed">
                        {addr.line1}
                        {addr.line2 && (
                          <>
                            <br />
                            {addr.line2}
                          </>
                        )}
                        <br />
                        {addr.city}, {addr.state} -{" "}
                        <span className="font-mono">{addr.pincode}</span>
                        {addr.landmark && (
                          <>
                            <br />
                            <span className="text-zinc-500 text-xs">
                              Landmark: {addr.landmark}
                            </span>
                          </>
                        )}
                      </div>
                      {canEditCustomer && linkedBook.length > 1 && (
                          <div className="mt-3 space-y-2" data-testid="order-address-picker">
                            <div className="font-mono text-[9px] tracking-[0.3em] text-zinc-500">
                              SAVED ADDRESSES
                            </div>
                            {linkedBook.map((saved) => {
                              const active =
                                (cust.address_id && cust.address_id === saved.id) ||
                                (addr &&
                                  addr.pincode === saved.pincode &&
                                  (addr.line1 || "").trim().toLowerCase() ===
                                    (saved.line1 || "").trim().toLowerCase());
                              return (
                                <button
                                  key={saved.id}
                                  type="button"
                                  disabled={savingCust || active}
                                  onClick={() => applySavedAddress(saved)}
                                  className={`w-full text-left p-2 border text-xs ${
                                    active
                                      ? "border-[color:var(--neon-cyan)] text-white"
                                      : "border-zinc-800 hover:border-white text-zinc-400"
                                  } disabled:opacity-60`}
                                >
                                  <span className="font-medium text-white">
                                    {saved.label || "Address"}
                                    {saved.is_default ? " · default" : ""}
                                  </span>
                                  <div className="mt-1 text-zinc-500">{formatAddressLine(saved)}</div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  )}
                  {!addr && canEditCustomer && linkedBook.length > 0 && (
                    <div className="pt-3 border-t border-zinc-800 space-y-2" data-testid="order-address-picker">
                      <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                        SAVED ADDRESSES
                      </div>
                      {linkedBook.map((saved) => (
                        <button
                          key={saved.id}
                          type="button"
                          disabled={savingCust}
                          onClick={() => applySavedAddress(saved)}
                          className="w-full text-left p-2 border border-zinc-800 hover:border-white text-xs text-zinc-400 disabled:opacity-60"
                        >
                          <span className="font-medium text-white">{saved.label || "Address"}</span>
                          <div className="mt-1 text-zinc-500">{formatAddressLine(saved)}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {order.is_returning_customer && (
                    <div className="font-mono text-[10px] tracking-widest text-[color:var(--neon-cyan)] border border-[color:var(--neon-cyan)] px-2 py-1 inline-block">
                      RETURNING CUSTOMER
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <div className="text-sm text-zinc-500">
                    No customer linked yet. Share the address link, or attach an existing CRM customer.
                  </div>
                  {canAttachCustomer && (
                    <button
                      type="button"
                      data-testid="attach-customer-empty"
                      onClick={openAttachCustomer}
                      className="btn-brutal w-full h-11 border border-zinc-700 hover:border-white text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2"
                    >
                      <UserPlus className="h-4 w-4" />
                      Attach existing customer
                    </button>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="card-brutal">
            <div className="p-5 border-b border-zinc-800 font-mono text-[10px] tracking-[0.3em] text-zinc-500">
              PAYMENT
            </div>
            <div className="p-5 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-400">Method</span>
                <span className="font-mono uppercase">{order.payment_method}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Status</span>
                <span className="font-mono text-[color:var(--success)]">
                  {order.payment_status?.toUpperCase()}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>

      <ManifestModal
        form={manifestOpen ? manifestForm : null}
        setForm={setManifestForm}
        orderNumber={order.order_number}
        onClose={() => setManifestOpen(false)}
        onSubmit={submitManifest}
        submitting={manifesting}
        error={manifestError}
      />

      {addItemsOpen && (
        <AddItemsModal
          products={products}
          productQ={productQ}
          setProductQ={setProductQ}
          addQty={addQty}
          setQtyFor={setQtyFor}
          onClose={() => setAddItemsOpen(false)}
          onSubmit={submitAddItems}
          submitting={addingItems}
          allowOos={order.status === "preorder"}
        />
      )}

      {attachOpen && (
        <AttachCustomerModal
          customers={customers}
          custQ={custQ}
          setCustQ={setCustQ}
          onClose={() => setAttachOpen(false)}
          onSelect={attachCustomer}
          submitting={attaching}
          title={order.customer_id ? "CHANGE CUSTOMER" : "ATTACH CUSTOMER"}
        />
      )}

      {editCustOpen && editCustForm && (
        <EditOrderCustomerModal
          form={editCustForm}
          setForm={setEditCustForm}
          addresses={linkedBook}
          hasCrmLink={Boolean(order.customer_id)}
          onClose={() => setEditCustOpen(false)}
          onSubmit={saveEditCustomer}
          submitting={savingCust}
        />
      )}
    </div>
  );
}

function AttachCustomerModal({ customers, custQ, setCustQ, onClose, onSelect, submitting, title }) {
  const [pending, setPending] = useState(null);
  const [addressId, setAddressId] = useState("");
  const filtered = (customers || []).filter((c) => {
    if (!custQ.trim()) return true;
    const t = custQ.toLowerCase();
    return (
      c.name?.toLowerCase().includes(t) ||
      c.phone?.includes(t) ||
      (c.email || "").toLowerCase().includes(t)
    );
  });

  const pickCustomer = (c) => {
    const book = customerAddressBook(c);
    if (book.length > 1) {
      setPending(c);
      const def = book.find((a) => a.is_default) || book[0];
      setAddressId(def?.id || "");
      return;
    }
    onSelect(c, book[0]?.id);
  };

  const confirmPending = () => {
    if (!pending) return;
    onSelect(pending, addressId);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="card-brutal w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800 shrink-0">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-[color:var(--mecha-red)]">
              // LINK
            </div>
            <h2 className="font-display text-2xl mt-1">
              {pending ? "PICK ADDRESS" : title || "ATTACH CUSTOMER"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {pending ? (
          <>
            <div className="p-5 space-y-3 overflow-y-auto flex-1">
              <div className="text-sm">
                {pending.name}
                <div className="font-mono text-[10px] text-zinc-500 mt-1">{pending.phone}</div>
              </div>
              {customerAddressBook(pending).map((addr) => (
                <label
                  key={addr.id}
                  className={`block p-3 border cursor-pointer ${
                    addressId === addr.id ? "border-[color:var(--neon-cyan)]" : "border-zinc-800"
                  }`}
                >
                  <input
                    type="radio"
                    name="attach-address"
                    className="mr-2"
                    checked={addressId === addr.id}
                    onChange={() => setAddressId(addr.id)}
                  />
                  <span className="text-sm font-medium">
                    {addr.label || "Address"}
                    {addr.is_default ? " · default" : ""}
                  </span>
                  <div className="text-xs text-zinc-400 mt-1 pl-6">{formatAddressLine(addr)}</div>
                </label>
              ))}
            </div>
            <div className="p-5 border-t border-zinc-800 shrink-0 flex gap-2">
              <button
                type="button"
                onClick={() => setPending(null)}
                className="flex-1 h-11 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase"
              >
                Back
              </button>
              <button
                type="button"
                data-testid="attach-address-confirm"
                disabled={submitting || !addressId}
                onClick={confirmPending}
                className="flex-1 h-11 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase disabled:opacity-60"
              >
                {submitting ? "Linking…" : "Use this address"}
              </button>
            </div>
          </>
        ) : (
          <>
        <div className="p-4 border-b border-zinc-800 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input
              data-testid="attach-customer-search"
              value={custQ}
              onChange={(e) => setCustQ(e.target.value)}
              placeholder="Search name, phone, email..."
              className="w-full h-11 bg-zinc-950 border border-zinc-800 pl-10 pr-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[color:var(--mecha-red)]"
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 divide-y divide-zinc-900">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">No customers found</div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`attach-customer-row-${c.phone}`}
                disabled={submitting}
                onClick={() => pickCustomer(c)}
                className="w-full text-left px-5 py-3 hover:bg-zinc-900/70 disabled:opacity-50"
              >
                <div className="text-sm font-medium truncate">{c.name}</div>
                <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                  {c.phone}
                  {c.email ? ` · ${c.email}` : ""}
                  {customerAddressBook(c).length > 1
                    ? ` · ${customerAddressBook(c).length} addresses`
                    : c.address?.city
                      ? ` · ${c.address.city}`
                      : ""}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="p-5 border-t border-zinc-800 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full h-11 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase"
          >
            Cancel
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
}

function EditOrderCustomerModal({ form, setForm, addresses, hasCrmLink, onClose, onSubmit, submitting }) {
  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const book = addresses || [];
  const selectAddress = (id) => {
    if (id === "new") {
      setForm((prev) => ({
        ...prev,
        address_id: "new",
        label: "Home",
        is_default: book.length === 0,
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        landmark: "",
      }));
      return;
    }
    const chosen = book.find((a) => a.id === id);
    if (!chosen) return;
    setForm((prev) => ({
      ...prev,
      address_id: chosen.id,
      label: chosen.label || "Home",
      is_default: Boolean(chosen.is_default),
      line1: chosen.line1 || "",
      line2: chosen.line2 || "",
      city: chosen.city || "",
      state: chosen.state || "",
      pincode: chosen.pincode || "",
      landmark: chosen.landmark || "",
    }));
  };
  const inputCls =
    "w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-sm text-white focus:outline-none focus:border-[color:var(--mecha-red)]";

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form
        onSubmit={onSubmit}
        className="card-brutal w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-[color:var(--mecha-red)]">
              // EDIT
            </div>
            <h2 className="font-display text-2xl mt-1">EDIT CUSTOMER</h2>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5">NAME *</div>
            <input
              data-testid="order-cust-name"
              required
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5">PHONE *</div>
            <input
              data-testid="order-cust-phone"
              required
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5">EMAIL</div>
            <input
              data-testid="order-cust-email"
              type="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              className={inputCls}
            />
          </label>

          <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 pt-2">ADDRESS</div>
          {book.length > 0 && (
            <div className="space-y-2" data-testid="order-cust-address-picker">
              <div className="text-xs text-zinc-400">
                Choose which saved address to edit, or add a new one.
              </div>
              {book.map((a) => (
                <label
                  key={a.id}
                  className={`block p-3 border cursor-pointer ${
                    form.address_id === a.id
                      ? "border-[color:var(--neon-cyan)]"
                      : "border-zinc-800"
                  }`}
                >
                  <input
                    type="radio"
                    name="order-edit-address"
                    className="mr-2"
                    checked={form.address_id === a.id}
                    onChange={() => selectAddress(a.id)}
                  />
                  <span className="text-sm font-medium">
                    {a.label || "Address"}
                    {a.is_default ? " · default" : ""}
                  </span>
                  <div className="text-xs text-zinc-400 mt-1 pl-6">{formatAddressLine(a)}</div>
                </label>
              ))}
              <label
                className={`block p-3 border cursor-pointer ${
                  form.address_id === "new" ? "border-[color:var(--neon-cyan)]" : "border-zinc-800"
                }`}
              >
                <input
                  type="radio"
                  name="order-edit-address"
                  className="mr-2"
                  checked={form.address_id === "new"}
                  onChange={() => selectAddress("new")}
                />
                <span className="text-sm font-medium">Add new address</span>
              </label>
            </div>
          )}
          <label className="block">
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5">LABEL</div>
            <input
              data-testid="order-cust-label"
              value={form.label || ""}
              onChange={(e) => setField("label", e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5">LINE 1</div>
            <input
              data-testid="order-cust-line1"
              value={form.line1}
              onChange={(e) => setField("line1", e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5">LINE 2</div>
            <input
              data-testid="order-cust-line2"
              value={form.line2}
              onChange={(e) => setField("line2", e.target.value)}
              className={inputCls}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5">CITY</div>
              <input
                data-testid="order-cust-city"
                value={form.city}
                onChange={(e) => setField("city", e.target.value)}
                className={inputCls}
              />
            </label>
            <label className="block">
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5">STATE</div>
              <input
                data-testid="order-cust-state"
                value={form.state}
                onChange={(e) => setField("state", e.target.value)}
                className={inputCls}
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5">PINCODE</div>
              <input
                data-testid="order-cust-pincode"
                value={form.pincode}
                onChange={(e) => setField("pincode", e.target.value)}
                className={inputCls}
              />
            </label>
            <label className="block">
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5">LANDMARK</div>
              <input
                data-testid="order-cust-landmark"
                value={form.landmark}
                onChange={(e) => setField("landmark", e.target.value)}
                className={inputCls}
              />
            </label>
          </div>
          {hasCrmLink && (
            <label className="flex items-start gap-3 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                data-testid="order-cust-default"
                checked={Boolean(form.is_default)}
                onChange={(e) => setField("is_default", e.target.checked)}
                className="mt-1"
              />
              <span>Default shipping address in CRM</span>
            </label>
          )}

          {hasCrmLink && (
            <label className="flex items-start gap-3 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                data-testid="order-cust-sync-crm"
                checked={Boolean(form.sync_crm)}
                onChange={(e) => setField("sync_crm", e.target.checked)}
                className="mt-1"
              />
              <span>
                Also update CRM customer record
                <span className="block text-xs text-zinc-500 mt-1">
                  Keeps this customer’s profile and other linked orders in sync.
                </span>
              </span>
            </label>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="order-cust-save"
              disabled={submitting}
              className="btn-brutal flex-1 h-11 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function AddItemsModal({
  products,
  productQ,
  setProductQ,
  addQty,
  setQtyFor,
  onClose,
  onSubmit,
  submitting,
  allowOos = false,
}) {
  const q = productQ.trim().toLowerCase();
  const filtered = (products || []).filter((p) => {
    if (!q) return true;
    return (
      p.name?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q)
    );
  });
  const selectedCount = Object.values(addQty).filter((n) => n > 0).length;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="card-brutal w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800 shrink-0">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-[color:var(--mecha-red)]">
              // ADD
            </div>
            <h2 className="font-display text-2xl mt-1">ADD PRODUCTS</h2>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 border-b border-zinc-800 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input
              data-testid="add-items-search"
              value={productQ}
              onChange={(e) => setProductQ(e.target.value)}
              placeholder="Search products..."
              className="w-full h-11 bg-zinc-950 border border-zinc-800 pl-10 pr-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[color:var(--mecha-red)]"
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 divide-y divide-zinc-900">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">No products found</div>
          ) : (
            filtered.map((p) => (
              <div
                key={p.id}
                className="px-5 py-3 flex items-center gap-3"
                data-testid={`add-item-row-${p.sku || p.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                    {p.sku} · {allowOos ? "Pre-order ok" : `Stock ${p.stock}`} · {fmtINR(p.price)}
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  max={allowOos ? undefined : p.stock}
                  value={addQty[p.id] || ""}
                  onChange={(e) => setQtyFor(p.id, e.target.value)}
                  placeholder="0"
                  className="w-16 h-9 bg-zinc-950 border border-zinc-800 text-center font-mono text-sm text-white focus:outline-none focus:border-[color:var(--mecha-red)]"
                />
              </div>
            ))
          )}
        </div>

        <div className="flex gap-3 p-5 border-t border-zinc-800 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="add-items-submit"
            onClick={onSubmit}
            disabled={submitting || selectedCount === 0}
            className="btn-brutal flex-1 h-11 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              `Add ${selectedCount || ""} product${selectedCount === 1 ? "" : "s"}`.trim()
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-zinc-400">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
        {label}
      </div>
      <div className={mono ? "font-mono text-sm" : "text-sm"}>{value}</div>
    </div>
  );
}

function WeightPill({ label, value, active, emphasize }) {
  const style = emphasize
    ? "border-[color:var(--neon-cyan)] bg-[color:var(--neon-cyan)]/20 text-[color:var(--neon-cyan)]"
    : active
    ? "border-[color:var(--mecha-red)] bg-[color:var(--mecha-red)]/15 text-white"
    : "border-zinc-800 text-zinc-500";
  return (
    <div className={`border p-2 text-center ${style}`}>
      <div className="font-mono text-[9px] tracking-widest">{label}</div>
      <div className="font-mono font-bold text-sm mt-0.5">{value}</div>
    </div>
  );
}

function ManifestModal({ form, setForm, orderNumber, onClose, onSubmit, submitting, error }) {
  const [rate, setRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState(null);

  const w = parseInt(form?.weight_g, 10) || 0;
  const L = parseFloat(form?.length_cm) || 0;
  const W = parseFloat(form?.width_cm) || 0;
  const H = parseFloat(form?.height_cm) || 0;
  const pin = String(form?.pincode || "");
  const paymode = form?.payment_mode;
  const smode = form?.shipping_mode;

  useEffect(() => {
    if (!form) return;
    if (!/^\d{6}$/.test(pin) || w <= 0) {
      setRate(null);
      setRateError(null);
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setRateLoading(true);
      setRateError(null);
      try {
        const payload = {
          destination_pincode: pin,
          weight_grams: w,
          payment_mode: paymode === "COD" ? "COD" : "Pre-paid",
          shipping_mode: form?.shipping_mode || "Surface",
        };
        if (L > 0 && W > 0 && H > 0) {
          payload.length_cm = L;
          payload.width_cm = W;
          payload.height_cm = H;
        }
        const { data } = await api.post("/api/shipping/rate", payload, { signal: controller.signal });
        setRate(data);
      } catch (e) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          setRateError(formatApiError(e.response?.data?.detail) || e.message);
          setRate(null);
        }
      } finally {
        setRateLoading(false);
      }
    }, 400);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [pin, w, L, W, H, paymode, smode, form]);

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls =
    "w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]";
  const labelCls = "block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1";

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="card-brutal w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800 shrink-0">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-[color:var(--mecha-red)]">
              // DELHIVERY LIVE MANIFEST
            </div>
            <h2 className="font-display text-2xl mt-1">
              MANIFEST {orderNumber}
            </h2>
          </div>
          <button
            data-testid="manifest-close"
            onClick={onClose}
            className="text-zinc-500 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto">
          <section>
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-2">
              CONSIGNEE
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>NAME</label>
                <input data-testid="mf-name" value={form.name} onChange={set("name")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>PHONE</label>
                <input data-testid="mf-phone" value={form.phone} onChange={set("phone")} className={inputCls} />
              </div>
            </div>
            <div className="mt-3">
              <label className={labelCls}>ADDRESS</label>
              <textarea
                data-testid="mf-address"
                value={form.address}
                onChange={set("address")}
                rows={2}
                className={inputCls + " h-auto py-2 resize-none"}
              />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <label className={labelCls}>PINCODE</label>
                <input data-testid="mf-pincode" value={form.pincode} onChange={set("pincode")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>CITY</label>
                <input data-testid="mf-city" value={form.city} onChange={set("city")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>STATE</label>
                <input data-testid="mf-state" value={form.state} onChange={set("state")} className={inputCls} />
              </div>
            </div>
          </section>

          <section>
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-2">
              PARCEL
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>WEIGHT G</label>
                <input data-testid="mf-weight" type="number" value={form.weight_g} onChange={set("weight_g")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>L (CM)</label>
                <input data-testid="mf-length" type="number" step="0.5" value={form.length_cm} onChange={set("length_cm")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>W (CM)</label>
                <input data-testid="mf-width" type="number" step="0.5" value={form.width_cm} onChange={set("width_cm")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>H (CM)</label>
                <input data-testid="mf-height" type="number" step="0.5" value={form.height_cm} onChange={set("height_cm")} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className={labelCls}>QUANTITY</label>
                <input data-testid="mf-qty" type="number" value={form.quantity} onChange={set("quantity")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>SHIPPING MODE</label>
                <select data-testid="mf-mode" value={form.shipping_mode} onChange={set("shipping_mode")} className={inputCls}>
                  <option value="Surface">Surface</option>
                  <option value="Express">Express</option>
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className={labelCls}>PRODUCT DESCRIPTION</label>
              <input data-testid="mf-desc" value={form.products_desc} onChange={set("products_desc")} className={inputCls} maxLength={150} />
            </div>
          </section>

          <section>
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-2">
              PAYMENT
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>MODE</label>
                <select data-testid="mf-paymode" value={form.payment_mode} onChange={set("payment_mode")} className={inputCls}>
                  <option value="Prepaid">Prepaid</option>
                  <option value="COD">COD</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>TOTAL AMOUNT</label>
                <input data-testid="mf-total" type="number" step="0.01" value={form.total_amount} onChange={set("total_amount")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>COD AMOUNT</label>
                <input
                  data-testid="mf-cod"
                  type="number"
                  step="0.01"
                  value={form.cod_amount}
                  onChange={set("cod_amount")}
                  disabled={form.payment_mode !== "COD"}
                  className={inputCls + " disabled:opacity-40"}
                />
              </div>
            </div>
          </section>

          {error && (
            <div
              data-testid="manifest-error"
              className="border border-[color:var(--mecha-red)] bg-[color:var(--mecha-red)]/10 p-3 text-xs text-[color:var(--mecha-red-hover)] whitespace-pre-wrap break-words"
            >
              {error}
            </div>
          )}
        </div>

        {/* Live Delhivery estimate strip */}
        <div className="border-t border-zinc-800 px-5 py-3 shrink-0 bg-zinc-950/50">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[9px] tracking-[0.3em] text-zinc-500">
                LIVE DELHIVERY ESTIMATE
              </div>
              {rateLoading ? (
                <div className="mt-1 flex items-center gap-2 text-xs text-zinc-400">
                  <Loader2 className="h-3 w-3 animate-spin" /> Fetching rate...
                </div>
              ) : rateError ? (
                <div data-testid="rate-error" className="mt-1 text-xs text-[color:var(--mecha-red-hover)] truncate">
                  {rateError}
                </div>
              ) : rate ? (
                <div className="mt-1 flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-[10px] tracking-widest text-zinc-400">
                    ZONE {rate.zone || "—"}
                  </span>
                  <span className="font-mono text-[10px] tracking-widest text-zinc-400">
                    CHARGED {rate.chargeable_weight_g || w} g
                  </span>
                  {rate.weight_basis === "volumetric" && (
                    <span className="font-mono text-[10px] tracking-widest text-[color:var(--warn)]">
                      VOLUMETRIC WINS
                    </span>
                  )}
                </div>
              ) : (
                <div className="mt-1 text-xs text-zinc-500">
                  Enter valid pincode + weight to see rate
                </div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono text-[9px] tracking-[0.3em] text-zinc-500">SHIPPING</div>
              <div
                data-testid="rate-total"
                className="font-display text-2xl text-[color:var(--neon-cyan)] leading-none mt-1"
              >
                {rate ? fmtINR(rate.total_amount || 0) : "—"}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-5 border-t border-zinc-800 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase"
          >
            Cancel
          </button>
          <button
            data-testid="manifest-submit"
            onClick={onSubmit}
            disabled={submitting}
            className="btn-brutal flex-1 h-11 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : rate ? (
              <>Create Waybill · {fmtINR(rate.total_amount || 0)}</>
            ) : (
              "Create Waybill"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
